<template>
  <div>
    <div class="content-wrapper">
      <CampaignTabs
        ref="campaignTabsRef"
        :active-tab="activeTab"
        @update:active-tab="handleTabChange"
      />
      <div class="filter-container">
        <div class="filter-title">
          Kampanyaları Filtrele
        </div>
        <div class="filter-content">
          <div class="filter-inputs">
            <div class="filter-input-wrapper">
              <Input
                id="campaign_name"
                v-model:value="campaignName"
                label="Kampanya"
                type="text"
              >
                <template #right-icon>
                  <Icons
                    name="Search"
                    size="md"
                    color="#B3BBCB"
                  />
                </template>
              </Input>
            </div>
            <div class="filter-input-wrapper category-dropdown">
              <DropdownSelectable
                ref="categoryDropdown"
                :drop-items="visibleCategoryOptions"
                label="Kategori Seç"
                not-found-text="Kategori bulunamadı."
                @selected-items="handleCategorySelection"
              />
            </div>
          </div>
          <div class="filter-buttons">
            <AppButton
              secondary
              md
              :disabled="!hasFilters"
              title="Temizle"
              @click="clearFilters"
            />
            <AppButton
              primary
              md
              title="Filtrele"
              @click="applyFilters"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="advanced-table-wrapper">
      <div class="table-area">
        <div class="actions-row">
          <div class="sort-wrapper">
            <Dropdown
              :key="sortDropdownKey"
              :position="'bottom-left'"
            >
              <template #dropdown-click>
                <button class="sort-button">
                  <Icons
                    name="Sort"
                    size="sm"
                    color="#1C1C1E"
                  />
                  <span class="sort-label">{{ selectedSortLabel }}</span>
                  <Icons
                    name="BottomArrows"
                    size="sm"
                    color="#7382A0"
                  />
                </button>
              </template>
              <template #dropdown-content>
                <ul class="sort-dropdown-content">
                  <li>
                    <button
                      type="button"
                      :class="{
                        active: sortBy === SORT_BY_OPTIONS.HIGHEST_DISCOUNT,
                      }"
                      @click="
                        handleSortChange(SORT_BY_OPTIONS.HIGHEST_DISCOUNT)
                      "
                    >
                      İndirim Oranı En Yüksek
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      :class="{
                        active: sortBy === SORT_BY_OPTIONS.NEAREST_START_DATE,
                      }"
                      @click="
                        handleSortChange(SORT_BY_OPTIONS.NEAREST_START_DATE)
                      "
                    >
                      Başlangıç Tarihi En Yakın
                    </button>
                  </li>
                </ul>
              </template>
            </Dropdown>
          </div>
          <div class="pagination-wrapper">
            <Pagination
              :page-count="pageCount"
              :selected-page-index="currentPage"
              :show-per-page="true"
              :per-page-items="PAGE_SIZES"
              :selected-per-page-key="pageSize"
              per-page-menu-position="top-left"
              @change-index="handlePageChange"
              @change-per-page="handlePageSizeChange"
            />
          </div>
        </div>
        <div class="table-wrapper">
          <table class="advanced-table">
            <thead>
              <tr v-if="activeTab === CAMPAIGN_TABS.AVAILABLE.text">
                <th>Kampanya Bilgisi</th>
                <th class="status-column-header">
                  <span>Kampanya Tarihleri</span>
                  <TooltipPlugin
                    :tooltip-text="CAMPAIGN_DATES_TOOLTIP_TEXT"
                    :tooltip-width="246"
                  >
                    <template #tooltip-btn>
                      <Icons
                        class="tooltip-icon"
                        name="InfoCircle"
                        color="#6B6B6B"
                        size="sm"
                      />
                    </template>
                  </TooltipPlugin>
                </th>
                <th>İşlemler</th>
              </tr>
              <tr v-else>
                <th>Kampanya Bilgisi</th>
                <th class="status-column-header">
                  <span>Kampanya Durumu</span>
                  <TooltipPlugin
                    :tooltip-text="STATUS_TOOLTIP_TEXT"
                    :tooltip-width="246"
                  >
                    <template #tooltip-btn>
                      <Icons
                        name="InfoCircle"
                        color="#7382a0"
                        size="sm"
                        class="tooltip-icon"
                      />
                    </template>
                  </TooltipPlugin>
                </th>
                <th>İşlemler</th>
              </tr>
            </thead>

            <tbody
              v-if="
                activeTab === CAMPAIGN_TABS.AVAILABLE.text && !isAvailableEmpty
              "
            >
              <template
                v-for="campaign in availableCampaigns"
                :key="campaign.id"
              >
                <tr>
                  <td>
                    <div class="campaign-name">
                      <div class="campaign-title">
                        {{ campaign.name }}
                      </div>
                      <div class="campaign-id">
                        <div class="campaign-id-label">
                          Kampanya ID:
                        </div>
                        <div class="campaign-id-value">
                          {{ campaign.id }}
                        </div>
                      </div>
                      <span class="commission-badge">%{{ campaign.commissionDiscountRate }} Komisyon
                        İndirimi</span>
                    </div>
                  </td>
                  <td>
                    <div class="campaign-dates">
                      <span
                        v-if="campaign.urgencyBadge"
                        class="status-badge"
                        :style="{
                          backgroundColor: getAvailableStatusConfig(
                            campaign.urgencyBadge,
                          ).colorCode,
                          color: getAvailableStatusConfig(campaign.urgencyBadge)
                            .textColorCode,
                        }"
                      >
                        {{
                          getAvailableStatusConfig(campaign.urgencyBadge).title
                        }}
                      </span>
                      <div class="date-range-container">
                        <div class="date-range-label">
                          Kampanya Katılım Tarih Aralığı:
                        </div>
                        <div class="date-range">
                          {{
                            formatTimestamp(campaign.startDate, {
                              spaceAfterDate: false,
                              dateHourSeparator: ',',
                            })
                          }}
                          -
                          {{
                            formatTimestamp(campaign.endDate, {
                              spaceAfterDate: false,
                              dateHourSeparator: ',',
                            })
                          }}
                        </div>
                        <div class="date-range-label">
                          Geçerlilik Süresi:
                        </div>
                        <div class="date-range">
                          {{ campaign.validityDayCount }} Gün
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="action-buttons">
                      <button
                        class="btn-join"
                        @click="clickOpenJoinCampaignModal(campaign)"
                      >
                        Kampanyaya Katıl
                      </button>
                      <button
                        class="btn-detail"
                        @click="goToCampaignDetail(campaign)"
                      >
                        Kampanya Detayına Git
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td
                    colspan="3"
                    class="category-list-td"
                  >
                    <div class="category-list">
                      <span class="category-list-title">Geçerli Kategoriler:</span>
                      <span class="category-list-items">{{
                        campaign?.allCategoryIncluded
                          ? 'Tüm Kategoriler'
                          : getTruncatedIncludedCategories(campaign)
                      }}</span>
                      <span
                        v-if="shouldShowAllCategoriesButton(campaign)"
                        class="category-list-view-all"
                        @click="openCategoryListModal(campaign)"
                      >Tümünü Gör</span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>

            <tbody
              v-if="
                activeTab === CAMPAIGN_TABS.AVAILABLE.text && isAvailableEmpty
              "
            >
              <tr>
                <td colspan="3">
                  <div class="empty-table-container">
                    <img
                      src="@/assets/images/empty-search.png"
                      alt="Kampanya bulunamadı"
                    >
                    <template v-if="lastFetchHadFilters">
                      <span class="empty-table-text">
                        Aradığınız kriterlere uygun bir kampanya bulunamadı.
                      </span>
                      <span class="empty-table-description">
                        Filtreleri temizleyip tekrar deneyebilirsiniz.
                      </span>
                    </template>
                    <template v-else>
                      <span class="empty-table-text">
                        Şu an katılabileceğiniz bir kampanya bulunmuyor.
                      </span>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>

            <tbody
              v-if="activeTab === CAMPAIGN_TABS.JOINED.text && !isJoinedEmpty"
            >
              <template
                v-for="campaign in joinedCampaigns"
                :key="campaign.id"
              >
                <tr>
                  <td>
                    <div class="campaign-name">
                      <div class="campaign-title">
                        {{ campaign.name }}
                      </div>
                      <div class="campaign-id">
                        <div class="campaign-id-label">
                          Kampanya ID:
                        </div>
                        <div class="campaign-id-value">
                          {{ campaign.id }}
                        </div>
                      </div>
                      <span class="commission-badge">%{{ campaign.commissionDiscountRate }} Komisyon
                        İndirimi</span>
                    </div>
                  </td>
                  <td>
                    <div class="joined-status-column">
                      <div class="campaign-status-badge-wrapper">
                        <CampaignStatusBadge
                          :status="campaign.participation.status"
                          :definitions="joinedCampaignDefinitions"
                        />
                        <div
                          v-if="
                            isStatusPlanned(campaign.participation.status) &&
                              campaign.participation.joinDate
                          "
                          class="campaign-status-description"
                        >
                          <span class="semi-bold">
                            {{
                              formatTimestamp(campaign.participation.joinDate, {
                                spaceAfterDate: false,
                                dateHourSeparator: ',',
                              })
                            }}
                          </span>
                          <span>tarihinde başlayacak</span>
                        </div>
                      </div>
                      <div class="joined-status-date">
                        {{
                          isStatusPlanned(campaign.participation.status)
                            ? 'Planlama Tarihi:'
                            : 'Katıldığınız Tarih:'
                        }}
                        <span>{{
                          formatTimestamp(
                            isStatusPlanned(campaign.participation.status)
                              ? campaign.participation.plannedDate
                              : campaign.participation.joinDate,
                            {
                              spaceAfterDate: false,
                              dateHourSeparator: ',',
                            },
                          )
                        }}</span>
                      </div>
                      <div class="status-item">
                        <div class="status-item-header">
                          <span class="label">Geçerlilik Süresi (Gün):</span>
                          <span class="value">{{ campaign.participation.daysElapsed }} /
                            {{ campaign.participation.totalDays }}</span>
                        </div>
                        <div class="status-item-progress">
                          <ProgressBar
                            :rectangle-value="
                              getProgressPercentage(
                                campaign.participation.daysElapsed,
                                campaign.participation.totalDays,
                              )
                            "
                            :show-percentage-on-zero="true"
                          />
                        </div>
                      </div>
                      <div
                        v-if="
                          campaign.participation.gmvUsage !== null &&
                            campaign.participation.gmvLimit !== null
                        "
                        class="status-item"
                      >
                        <div class="status-item-header">
                          <span class="label">Kampanya Toplam Satış Limiti (₺):</span>
                          <span class="value">{{
                            formatNumberBasic(campaign.participation.gmvUsage)
                          }}
                            /
                            {{
                              formatNumberBasic(campaign.participation.gmvLimit)
                            }}</span>
                        </div>
                        <div class="status-item-progress">
                          <ProgressBar
                            :rectangle-value="
                              campaign.participation.gmvUsagePercentage
                            "
                            :show-percentage-on-zero="true"
                          />
                        </div>
                      </div>
                      <div
                        v-if="
                          campaign.participation.orderUsage !== null &&
                            campaign.participation.orderLimit !== null
                        "
                        class="status-item"
                      >
                        <div class="status-item-header">
                          <span class="label">Kampanya Sipariş Limiti (Adet):</span>
                          <span class="value">{{ campaign.participation.orderUsage }} /
                            {{ campaign.participation.orderLimit }}</span>
                        </div>
                        <div class="status-item-progress">
                          <ProgressBar
                            :rectangle-value="
                              campaign.participation.orderUsagePercentage
                            "
                            :show-percentage-on-zero="true"
                          />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="action-buttons">
                      <button
                        class="btn-detail"
                        @click="goToCampaignDetail(campaign)"
                      >
                        Kampanya Detayına Git
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td
                    colspan="3"
                    class="category-list-td"
                  >
                    <div class="category-list">
                      <span class="category-list-title">Geçerli Kategoriler:</span>
                      <span class="category-list-items">{{
                        campaign?.allCategoryIncluded
                          ? 'Tüm Kategoriler'
                          : getTruncatedIncludedCategories(campaign)
                      }}</span>
                      <span
                        v-if="shouldShowAllCategoriesButton(campaign)"
                        class="category-list-view-all"
                        @click="openCategoryListModal(campaign)"
                      >Tümünü Gör</span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>

            <tbody
              v-if="activeTab === CAMPAIGN_TABS.JOINED.text && isJoinedEmpty"
            >
              <tr>
                <td colspan="3">
                  <div class="empty-table-container">
                    <img
                      src="@/assets/images/empty-search.png"
                      alt="Kampanya bulunamadı"
                    >
                    <template v-if="lastFetchHadFilters">
                      <span class="empty-table-text">
                        Aradığınız kriterlere uygun bir kampanya bulunamadı.
                      </span>
                      <span class="empty-table-description">
                        Filtreleri temizleyip tekrar deneyebilirsiniz.
                      </span>
                    </template>
                    <template v-else>
                      <span class="empty-table-text">
                        Henüz bir kampanyaya katılmadınız.
                      </span>
                      <span class="empty-table-description">
                        Katılabileceğiniz kampanyalar sekmesinden tüm yeni
                        fırsatları takip edebilirsiniz.
                      </span>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="pagination bottom-pagination">
          <Pagination
            :page-count="pageCount"
            :selected-page-index="currentPage"
            :show-per-page="true"
            :per-page-items="PAGE_SIZES"
            :selected-per-page-key="pageSize"
            per-page-menu-position="top-left"
            @change-index="handlePageChange"
            @change-per-page="handlePageSizeChange"
          />
        </div>
      </div>
    </div>

    <ModalJoinCampaign
      ref="joinModalRef"
      @join-success="onJoinSuccessCampaign"
      @join-dismiss="onJoinDismissCampaign"
      @join-close="onJoinCloseClickCampaign"
    />
    <ModalCategoryList ref="categoryListModalRef" />
    <ModalCampaignEnded ref="campaignEndedModalRef" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  Icons,
  Input,
  DropdownSelectable,
  AppButton,
  Dropdown,
  Pagination,
  ProgressBar,
  TooltipPlugin,
} from '@n11/so-ui-components';
import { formatNumberBasic } from '../../../utils/formatNumber';
import { formatTimestamp } from '../../../utils/formatedDate';
import {
  sendCategoryCommissionCampaignEvent,
  mapCampaignToGA4Params,
  sendViewCampaignDetailClickEvent,
  sendSortApplyComissionEvent,
  sendFilterShowResultsComissionEvent,
  sendPageViewEvent,
} from '../../../utils/ga4/helper';
import { GA4_EVENT } from '../../../utils/ga4/event';
import {
  PAGE_TYPE,
  STATUS_TOOLTIP_TEXT,
  CAMPAIGN_DATES_TOOLTIP_TEXT,
  PAGE_SIZES,
  CAMPAIGN_TABS,
  SORT_BY_OPTIONS,
  JOINED_STATUS_TYPES,
} from '../constants';
import {
  CampaignTabs,
  ModalJoinCampaign,
  ModalCategoryList,
  ModalCampaignEnded,
  CampaignStatusBadge,
} from '.';
import {
  useCategoryCommissionCampaignsAvailable,
  useCategoryCommissionCampaignsJoined,
  useCategoryCommissionCampaignDetail,
  useCategoryCommissionCampaignsFilterCategories,
} from '../services';

const router = useRouter();

const { data: availableData, fetchData: fetchAvailableData } =
  useCategoryCommissionCampaignsAvailable();

const { data: joinedData, fetchData: fetchJoinedData } =
  useCategoryCommissionCampaignsJoined();

const {
  data: campaignDetailData,
  error: campaignDetailError,
  fetchById: fetchCampaignDetail,
} = useCategoryCommissionCampaignDetail();

const { data: filterCategoriesData, fetchData: fetchFilterCategories } =
  useCategoryCommissionCampaignsFilterCategories();

const store = useStore();
const sellerInfo = computed(() => ({
  sellerId: store.state.auth.user.sellerId || '',
  sellerName: store.state.auth.user.username || '',
}));

const activeTab = ref(CAMPAIGN_TABS.AVAILABLE.text);
const campaignName = ref('');

const categoryDropdown = ref(null);
const selectedCategories = ref([]);
const sortBy = ref(SORT_BY_OPTIONS.NEAREST_START_DATE);
const hasSelectedSort = ref(false);
const sortDropdownKey = ref(0);
const currentPage = ref(0);
const pageSize = ref(10);
const pageCount = ref(1);
const categoryOptions = ref([]);
const lastFetchHadFilters = ref(false);

const availableCampaigns = ref([]);
const availableCampaignDefinitions = ref([]);
const joinedCampaigns = ref([]);
const joinedCampaignDefinitions = ref([]);

const getQueryParams = () => ({
  sortBy: sortBy.value,
  page: currentPage.value,
  size: pageSize.value,
  categoryIds: selectedCategories.value.map((cat) => cat.id ?? cat),
  campaignName: campaignName.value.trim(),
});

const fetchAvailableCampaigns = async () => {
  await fetchAvailableData(getQueryParams());
  availableCampaigns.value = availableData.value?.campaigns ?? [];
  availableCampaignDefinitions.value =
    availableData.value?.definition?.urgencyBadges ?? [];
  pageCount.value = availableData?.value?.pagingModel?.pageCount || 1;
};

const fetchJoinedCampaigns = async () => {
  await fetchJoinedData(getQueryParams());
  joinedCampaigns.value = joinedData.value?.campaigns ?? [];
  joinedCampaignDefinitions.value =
    joinedData.value?.definition?.sellerStatusDefinitions ?? [];
  pageCount.value = joinedData?.value?.pagingModel?.pageCount || 1;
};

const fetchCampaignsByTab = async (tab, { resetPage = true } = {}) => {
  if (resetPage) {
    currentPage.value = 0;
  }

  lastFetchHadFilters.value = hasFilters.value;

  if (tab === CAMPAIGN_TABS.AVAILABLE.text) {
    await fetchAvailableCampaigns();

    return;
  }

  await fetchJoinedCampaigns();
};

const resetFilterState = () => {
  campaignName.value = '';
  selectedCategories.value = [];
  if (categoryDropdown.value) {
    categoryDropdown.value.clearSelection();
  }
};

const handleTabChange = (tab) => {
  resetFilterState();
  hasSelectedSort.value = false;
  currentPage.value = 0;
  activeTab.value = tab;
  router.push({ query: { tab } });
  fetchCampaignsByTab(tab);
};

const isAvailableEmpty = computed(() => availableCampaigns.value.length === 0);
const isJoinedEmpty = computed(() => joinedCampaigns.value.length === 0);
const isStatusPlanned = (status) => status === JOINED_STATUS_TYPES.PLANNED;

const hasFilters = computed(() => {
  return (
    campaignName.value.trim().length > 0 || selectedCategories.value.length > 0
  );
});

const visibleCategoryOptions = computed(() => {
  const isActiveTabEmpty =
    activeTab.value === CAMPAIGN_TABS.AVAILABLE.text
      ? isAvailableEmpty.value
      : isJoinedEmpty.value;

  if (isActiveTabEmpty && !hasFilters.value) {
    return [];
  }

  return categoryOptions.value;
});

const selectedSortLabel = computed(() => {
  if (!hasSelectedSort.value) {
    return 'Sırala';
  }

  if (sortBy.value === SORT_BY_OPTIONS.HIGHEST_DISCOUNT) {
    return 'İndirim Oranı En Yüksek';
  }

  return 'Başlangıç Tarihi En Yakın';
});

const getAvailableStatusConfig = (status) => {
  const definition = availableCampaignDefinitions.value.find(
    (item) => item.code === status,
  );

  return {
    colorCode: definition?.colorCode,
    textColorCode: definition?.textColorCode,
    title: definition?.title,
  };
};

const getProgressPercentage = (current, total) => {
  if (!total) return 0;
  return Math.round((current / total) * 100);
};

const getTruncatedIncludedCategories = (campaign, maxLength = 156) => {
  const includedCategoryNames =
    campaign?.includedCategories?.map((category) => category.name).join(', ') ??
    '';

  if (includedCategoryNames.length <= maxLength) {
    return includedCategoryNames;
  }

  return `${includedCategoryNames.slice(0, maxLength)}...`;
};

const shouldShowAllCategoriesButton = (campaign) => {
  const includedCategoryNames =
    campaign?.includedCategories?.map((category) => category.name).join(', ') ??
    '';
  const maxLength = 156;

  const hasExcludedCategories = campaign?.excludedCategories?.length > 0;
  const isAllCategoryIncluded = campaign?.allCategoryIncluded;
  const isIncludedCategoriesLongerThanMaxLength = !isAllCategoryIncluded
    ? includedCategoryNames.length > maxLength
    : false;

  return isIncludedCategoriesLongerThanMaxLength || hasExcludedCategories;
};

const handleCategorySelection = (items) => {
  selectedCategories.value = items;
};

const clearFilters = () => {
  resetFilterState();
  currentPage.value = 0;
  fetchCampaignsByTab(activeTab.value);
};

const applyFilters = async () => {
  currentPage.value = 0;
  await fetchCampaignsByTab(activeTab.value);

  const campaigns =
    activeTab.value === CAMPAIGN_TABS.AVAILABLE.text
      ? availableCampaigns.value
      : joinedCampaigns.value;

  const categoryNames = selectedCategories.value
    .map((cat) => cat.text ?? cat.name ?? cat)
    .join('|');

  sendFilterShowResultsComissionEvent({
    search_term: campaignName.value.trim(),
    selected_categories: categoryNames,
    selected_category_count: selectedCategories.value.length,
    result_campaign_count: campaigns.length,
    page_type: PAGE_TYPE,
    seller_name: sellerInfo.value.sellerName,
    seller_id: sellerInfo.value.sellerId,
  });
};

const joinModalRef = ref(null);
const categoryListModalRef = ref(null);
const campaignEndedModalRef = ref(null);
const campaignTabsRef = ref(null);

const fetchAndValidateCampaignDetail = async (campaignId) => {
  await fetchCampaignDetail(campaignId);

  if (campaignDetailError.value || !campaignDetailData.value) return false;

  return true;
};

const onJoinSuccessCampaign = () => {
  sendCategoryCommissionCampaignEvent(
    GA4_EVENT.JOIN_CAMPAIGN_SUCCESS_COMISSION,
    mapCampaignToGA4Params(campaignDetailData.value, sellerInfo.value),
  );

  fetchAvailableCampaigns();
  campaignTabsRef.value?.getCountsData();
};

const onJoinDismissCampaign = () => {
  sendCategoryCommissionCampaignEvent(GA4_EVENT.JOIN_CAMPAIGN_CANCEL_CLICK, {
    ...mapCampaignToGA4Params(campaignDetailData.value, sellerInfo.value),
    page_type: PAGE_TYPE,
    tab: CAMPAIGN_TABS.AVAILABLE.defaultTitle,
  });
};

const onJoinCloseClickCampaign = () => {
  sendCategoryCommissionCampaignEvent(GA4_EVENT.JOIN_CAMPAIGN_CLOSE_CLICK, {
    ...mapCampaignToGA4Params(campaignDetailData.value, sellerInfo.value),
    page_type: PAGE_TYPE,
    tab: 'Katılabileceğiniz Kampanyalar',
  });
};

const clickOpenJoinCampaignModal = async (campaign) => {
  const isValid = await fetchAndValidateCampaignDetail(campaign.id);
  if (!isValid) return;

  sendCategoryCommissionCampaignEvent(
    GA4_EVENT.JOIN_CAMPAIGN_CLICK,
    mapCampaignToGA4Params(campaignDetailData.value, sellerInfo.value),
  );

  joinModalRef.value?.open?.({
    ...campaignDetailData.value,
    joinContext: {
      type: 'campaignId',
      value: campaign.id,
    },
  });
};

const openCategoryListModal = (campaign) => {
  categoryListModalRef.value?.open?.(campaign);
};

const handleSortChange = (sortType) => {
  sortDropdownKey.value += 1;
  hasSelectedSort.value = true;
  if (sortBy.value === sortType) return;
  sortBy.value = sortType;
  currentPage.value = 0;

  const sortingOptionLabel =
    sortType === SORT_BY_OPTIONS.HIGHEST_DISCOUNT
      ? 'İndirim Oranı En Yüksek'
      : 'Başlangıç Tarihi En Yakın';

  const tabLabel =
    activeTab.value === CAMPAIGN_TABS.AVAILABLE.text
      ? CAMPAIGN_TABS.AVAILABLE.defaultTitle
      : CAMPAIGN_TABS.JOINED.defaultTitle;

  sendSortApplyComissionEvent({
    page_type: PAGE_TYPE,
    tab: tabLabel,
    sorting_options: sortingOptionLabel,
    seller_name: sellerInfo.value.sellerName,
    seller_id: sellerInfo.value.sellerId,
  });

  fetchCampaignsByTab(activeTab.value);
};

const handlePageChange = (pageIndex) => {
  const next = Number(pageIndex);
  if (next === currentPage.value) return;
  currentPage.value = next;
  fetchCampaignsByTab(activeTab.value, { resetPage: false });
};

const handlePageSizeChange = (newSize) => {
  pageSize.value = Number(newSize);
  currentPage.value = 0;

  fetchCampaignsByTab(activeTab.value);
};

const goToCampaignDetail = async (campaign) => {
  const isValid = await fetchAndValidateCampaignDetail(campaign.id);
  if (!isValid) return;

  const isJoinedTab = activeTab.value === CAMPAIGN_TABS.JOINED.text;
  const tab = isJoinedTab
    ? CAMPAIGN_TABS.JOINED.defaultTitle
    : CAMPAIGN_TABS.AVAILABLE.defaultTitle;

  sendViewCampaignDetailClickEvent(campaignDetailData.value, sellerInfo.value, {
    page_type: PAGE_TYPE,
    tab,
  });

  sendPageViewEvent({
    page_type: PAGE_TYPE,
    seller_name: sellerInfo.value.sellerName,
    seller_id: sellerInfo.value.sellerId,
  });

  const routeName = isJoinedTab
    ? 'CategoryCommissionCampaignDetailJoined'
    : 'CategoryCommissionCampaignDetail';

  router.push({
    name: routeName,
    params: { id: campaign.id },
  });
};

const fetchFilterCategoryOptions = async () => {
  await fetchFilterCategories();
  categoryOptions.value = filterCategoriesData.value ?? [];
};

onMounted(() => {
  fetchFilterCategoryOptions();
});

watch(
  () => router.currentRoute.value.query,
  (newQuery) => {
    const { tab } = newQuery;
    if (tab) {
      activeTab.value = tab;
    }
    fetchCampaignsByTab(activeTab.value);
  },
  { immediate: true },
);

defineExpose({
  fetchCampaignsByTab,
  activeTab,
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';

.status-item {
  .status-item-header {
    display: flex;
    height: 14px;
    align-items: center;
    justify-content: space-between;
    margin: 6px 0 4px;

    .label {
      color: $colorN60;
      font-size: 11px;
      font-weight: 400;
    }

    .value {
      margin-top: 4px;
      color: $colorN80;
      font-size: 12px;
      font-weight: 600;
    }
  }

  .status-item-progress {
    :deep(.progress-container) {
      gap: 4px;
    }

    :deep(.result) {
      display: none;
      font-size: 11px;
    }

    :deep(.progress) {
      gap: 8px;
    }

    :deep(.rectangle) {
      width: 100%;
      padding-right: 0;
      background-color: $colorM05;
    }
  }
}

.content-wrapper {
  display: flex;
  width: 100%;
  flex-direction: column;
  border-radius: 8px;
  border-bottom: 0.5px solid $colorN30;
  margin-top: 24px;
  background: $colorN00;

  .tabs-container {
    display: flex;
    padding: 16px 24px;
    border-bottom: 0.5px solid $colorN30;
    gap: 12px;

    .tab-button {
      display: flex;
      min-height: 64px;
      flex: 1;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 12px 24px;
      border: 0.5px solid $colorD30;
      border-radius: 6px;
      background: $colorN00;
      color: $colorN80;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      gap: 4px;
      transition: all 0.2s;

      .label {
        color: $colorN80;
        cursor: pointer;
        font-size: 14px;
        font-weight: 700;
        letter-spacing: -0.05px;
        line-height: 18px;
        text-align: center;
      }

      .count {
        color: $colorN60;
        font-size: 14px;
        font-weight: 700;
        letter-spacing: -0.05px;
        line-height: 18px;
        text-align: center;
      }

      &.active {
        border: 2px solid $colorM50;
        background: $colorM05;
        color: $colorN80;
        font-weight: 600;

        .label,
        .count {
          color: $colorN80;
        }
      }
    }
  }

  .filter-container {
    display: flex;
    flex-direction: column;
    padding: 16px 24px;
    gap: 16px;

    .filter-title {
      padding: 0;
      border: none;
      color: $colorN80;
      font-size: 16px;
      font-weight: 700;
      letter-spacing: -0.05px;
      line-height: 20px;
    }

    .filter-content {
      display: flex;
      align-items: flex-end;
      padding: 0;
      gap: 16px;
    }

    .filter-inputs {
      display: flex;
      flex: 1;
      align-items: center;
      gap: 16px;

      .filter-input-wrapper {
        flex: 1;
      }

      .category-dropdown :deep(.notFoundText) {
        text-align: left;
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
      }
    }

    .filter-buttons {
      display: flex;
      justify-content: flex-end;
      gap: 16px;
    }
  }
}

.advanced-table-wrapper {
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  padding: 24px;
  border-radius: 8px;
  margin-top: 24px;
  margin-bottom: 100px;
  background: $colorN00;
  gap: 24px;

  .table-area {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .actions-row {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .sort-wrapper {
        position: relative;
        z-index: 3;

        .sort-button {
          display: flex;
          align-items: center;
          padding: 8px 12px;
          border: 0.5px solid $colorN30;
          border-radius: 6px;
          background: $colorN00;
          color: $colorN80;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          gap: 8px;

          .sort-label {
            color: $colorN80;
          }
        }

        .sort-dropdown-content {
          min-width: 200px;
          border: 1px solid $colorN50;
          border-radius: 6px;
          margin: 0;
          background: $colorN00;
          box-shadow: 0 2px 8px rgb(0 0 0 / 10%);
          box-shadow: 0 0 0 3px rgb(180 180 180 / 30%);
          list-style: none;

          li {
            button {
              width: 100%;
              padding: 8px 10px;
              border: none;
              background: transparent;
              color: $colorN80;
              cursor: pointer;
              font-size: 12px;
              text-align: left;
              transition: background 0.2s;

              &.active {
                background-color: $colorN10;
                font-size: 12px;
                font-weight: 700;
                line-height: 16px;

                &:first-child {
                  border-top-left-radius: 6px;
                  border-top-right-radius: 6px;
                }

                &:last-child {
                  border-bottom-left-radius: 6px;
                  border-bottom-right-radius: 6px;
                }
              }
            }

            &:not(:last-child) {
              border-bottom: 1px solid $colorN30;
            }
          }
        }
      }

      .pagination-wrapper {
        display: flex;
        align-items: center;
      }
    }

    .table-wrapper {
      overflow: hidden;
      width: 100%;
      border: 1px solid $colorN50;
      border-radius: 8px;
    }

    .advanced-table {
      width: 100%;
      border-collapse: collapse;

      thead {
        border-bottom: 2px solid $colorN30;
        background: $colorN00;

        th {
          position: sticky !important;
          z-index: 2 !important;
          top: 0;
          padding: 7px 12px;
          background-color: $colorBe30;
          color: $colorN80;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: -0.05px;
          line-height: 18px;
          text-align: left;
          vertical-align: middle;

          &:not(:last-child) {
            border-right: 1px solid $colorN30;
          }
        }
      }

      tbody {
        tr {
          td {
            border-bottom: none !important;
            background-color: $colorN00;
            color: $colorN80;
            font-size: 13px;
            letter-spacing: -0.05px;
            line-height: 18px;
            vertical-align: middle;

            &:not(.category-list-td) {
              border-top: 0.5px solid $colorN30;
            }

            &:not(:last-child) {
              border-right: 1px solid $colorN30;
            }

            &:nth-child(2) {
              width: 420px;
            }

            &:last-child {
              width: 197px;
            }
          }

          &:not(:last-child) {
            td {
              border-bottom: 2px solid $colorN30;
            }
          }
        }
      }
    }

    .campaign-name {
      display: flex;
      flex-direction: column;
      padding: 16px 12px;
      gap: 6px;

      .campaign-title {
        color: $colorN80;
        font-weight: 600;
      }

      .commission-badge {
        display: inline-block;
        width: fit-content;
        padding: 4px 8px;
        border: 1px solid $colorN30;
        border-radius: 4px;
        background: $colorN00;
        color: #6b6b6b;
        font-size: 11px;
        font-weight: 600;
        line-height: 14px;
        text-align: center;
      }
    }

    .campaign-id {
      display: flex;
      gap: 4px;

      .campaign-id-label {
        font-size: 13px;
      }
    }

    .campaign-dates {
      display: flex;
      flex-direction: column;
      padding: 16px 12px;

      .status-badge {
        display: inline-block;
        width: fit-content;
        height: 24px;
        padding: 2px 8px;
        border: 0.5px solid $colorN30;
        border-radius: 4px;
        margin-bottom: 6px;
        color: $colorN80;
        font-size: 11px;
        font-weight: 600;

        &--last-chance {
          background: $colorA40;
        }

        &--new-started {
          background: $colorY40;
        }

        &--starting-soon {
          background: $colorL50;
        }
      }

      .date-range-container {
        display: flex;
        flex-direction: column;
      }

      .date-range-label {
        color: $colorN60;
        font-size: 11px;
        font-weight: 400;
      }

      .date-range {
        color: $colorN80;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: -0.05px;
        line-height: 18px;
      }
    }

    .action-buttons {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;

      .btn-detail,
      .btn-join {
        width: 149px;
        height: 24px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 11px;
        font-weight: 600;
        line-height: 14px;
        white-space: nowrap;
      }

      .btn-detail {
        border: 1px solid $colorN30;
        background: $colorN00;
        color: $colorN80;
        font-style: normal;
      }

      .btn-join {
        border: none;
        background: $colorN80;
        color: $colorN00;
      }
    }

    .category-list-td {
      padding: 0 12px 16px;
    }

    .category-list {
      display: flex;
      width: 1245px;
      flex-direction: row;
      align-items: flex-start;
      padding: 12px 0;
      padding: 12px 10px;
      border-radius: 6px;
      background-color: $colorN10;
      gap: 8px;

      .category-list-title {
        color: $colorN70;
        font-size: 13px;
        font-weight: 400;
        letter-spacing: -0.05px;
        line-height: 18px;
        white-space: nowrap;
      }

      .category-list-items {
        overflow: hidden;
        flex: 1;
        color: $colorN70;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: -0.05px;
        line-height: 18px;
        white-space: nowrap;
      }

      .category-list-view-all {
        color: $colorM50;
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: -0.05px;
        line-height: 18px;
        white-space: nowrap;
      }
    }

    .empty-table-container {
      display: flex;
      height: 624px;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 64px 24px;
      gap: 12px;

      img {
        width: 120px;
        height: 120px;
        margin-bottom: 12px;
      }

      .empty-table-text {
        color: $colorN80;
        font-size: 16px;
        font-weight: 700;
        letter-spacing: -0.05px;
        line-height: 20px;
        text-align: center;
      }

      .empty-table-description {
        color: $colorN80;
        font-size: 16px;
        font-weight: 600;
        letter-spacing: -0.05px;
        line-height: 20px;
        text-align: center;
      }
    }

    .bottom-pagination {
      display: flex;
      justify-content: end;
      padding-top: 16px;
    }
  }
}

:deep(.modal-body) {
  padding: 0 16px 0 0 !important;
  margin-right: -16px !important;
}

:deep(.modal-footer) {
  padding: 0 !important;
}

.status-column-header {
  display: flex;
  align-items: end;
  gap: 4px;

  .tooltip-icon {
    cursor: pointer;
  }
}

.joined-status-column {
  display: flex;
  flex-direction: column;
  padding: 16px 12px;
}

.joined-status-badge {
  height: 24px;
  align-self: flex-start;
  padding: 4px 8px;
  border: 0.5px solid $colorN30;
  border-radius: 4px;
  margin-bottom: 8px;
  background-color: $colorB05;
  color: $colorB60;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
}

.joined-status-date {
  margin-bottom: 6px;
  margin-top: 8px;
  font-size: 13px;
  font-weight: 400;
  letter-spacing: -0.05px;
  line-height: 18px;

  span {
    font-weight: 600;
  }
}

.campaign-status-badge-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.campaign-status-description {
  color: $colorY60;
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: Open Sans;
  font-weight: 400;
  font-style: Regular;
  font-size: 11px;
  line-height: 14px;
  vertical-align: middle;
}
</style>
